import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash, ChartLine, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const QTYPES = { text: "Metin", rating: "Puan (1-5)", yesno: "Evet/Hayır", choice: "Çoktan Seçmeli" };
const DEPTS = ["Ön Büro", "Kat Hizmetleri", "Yiyecek & İçecek", "Mutfak", "Teknik Servis", "Güvenlik", "Muhasebe", "İK", "Kurumsal İletişim", "Satın Alma", "Yönetim"];

export default function Surveys() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_department: "", anonymous: false, questions: [{ text: "", type: "text", options: [] }] });
  const [responseFor, setResponseFor] = useState(null);
  const [answers, setAnswers] = useState({});
  const [viewResponses, setViewResponses] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMeta, setImportMeta] = useState({ title: "", target_department: "", anonymous: false });
  const [importing, setImporting] = useState(false);
  const isAdmin = ["admin", "hr"].includes(user?.role);

  const load = () => api.get("/surveys").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/surveys/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "anket_sablonu.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const importSurvey = async () => {
    if (!importFile) return toast.error("Lütfen bir Excel dosyası seçin");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      if (importMeta.title.trim()) fd.append("title", importMeta.title.trim());
      if (importMeta.target_department) fd.append("target_department", importMeta.target_department);
      fd.append("anonymous", importMeta.anonymous ? "Evet" : "");
      await api.post("/surveys/import", fd);
      setImportOpen(false);
      setImportFile(null);
      setImportMeta({ title: "", target_department: "", anonymous: false });
      load();
      toast.success("Anket Excel'den oluşturuldu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarma başarısız");
    } finally { setImporting(false); }
  };

  const create = async () => {
    if (!form.title || form.questions.length === 0 || form.questions.some((q) => !q.text)) return toast.error("Başlık ve tüm sorular gerekli");
    await api.post("/surveys", { ...form, target_department: form.target_department || null });
    setOpen(false);
    setForm({ title: "", description: "", target_department: "", anonymous: false, questions: [{ text: "", type: "text", options: [] }] });
    load();
    toast.success("Anket yayınlandı");
  };

  const respond = async () => {
    const payload = responseFor.questions.map((q, i) => answers[i] ?? "");
    if (payload.some((a) => a === "")) return toast.error("Tüm soruları yanıtlayın");
    await api.post(`/surveys/${responseFor.id}/respond`, { answers: payload });
    toast.success("Yanıtınız kaydedildi");
    setResponseFor(null); setAnswers({});
    load();
  };

  const openResponses = async (s) => {
    const { data } = await api.get(`/surveys/${s.id}/responses`);
    setViewResponses({ survey: s, responses: data });
  };

  const del = async (id) => { await api.delete(`/surveys/${id}`); load(); };

  const setQ = (i, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Geri Bildirim</div>
          <h1 className="font-display text-3xl font-bold mt-1">Anketler</h1>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="import-survey-btn"><UploadSimple size={16} className="mr-1" />Excel'den Aç</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Excel'den Anket Oluştur</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Şablonu indirip doldurun: başlık/açıklama/departman/anonim bilgisini üst satırlara,
                  soruları <b>Soru · Tip · Seçenekler</b> tablosuna yazın. Tip: Metin / Puan / Evet/Hayır / Çoktan Seçmeli.
                </div>
                <Button variant="secondary" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
                  <DownloadSimple size={16} className="mr-1" />Örnek Şablonu İndir
                </Button>
                <div>
                  <Label>Excel Dosyası (.xlsx)</Label>
                  <Input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="survey-file-input" />
                </div>
                <div className="border-t border-border pt-3 space-y-3">
                  <div className="text-xs text-muted-foreground">İsteğe bağlı: aşağıdaki alanlar Excel'deki meta bilgisini ezer.</div>
                  <div><Label>Başlık (opsiyonel)</Label><Input value={importMeta.title} onChange={(e) => setImportMeta({ ...importMeta, title: e.target.value })} placeholder="Boş bırakılırsa Excel'den alınır" data-testid="import-title" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Hedef Departman (opsiyonel)</Label>
                      <Select value={importMeta.target_department || "all"} onValueChange={(v) => setImportMeta({ ...importMeta, target_department: v === "all" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Tüm çalışanlar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tüm çalışanlar</SelectItem>
                          {DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <Switch checked={importMeta.anonymous} onCheckedChange={(v) => setImportMeta({ ...importMeta, anonymous: v })} data-testid="import-anon" />
                      <Label>Anonim yanıtla</Label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={importSurvey} disabled={importing} data-testid="import-submit">{importing ? "Yükleniyor…" : "İçe Aktar ve Yayınla"}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-survey-btn"><Plus size={16} className="mr-1" />Yeni Anket</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Yeni Anket</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="s-title" /></div>
                <div><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="s-desc" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Hedef Departman (opsiyonel)</Label>
                    <Select value={form.target_department} onValueChange={(v) => setForm({...form, target_department: v === "all" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Tüm çalışanlar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm çalışanlar</SelectItem>
                        {DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Switch checked={form.anonymous} onCheckedChange={(v) => setForm({...form, anonymous: v})} data-testid="s-anon" />
                    <Label>Anonim yanıtla</Label>
                  </div>
                </div>
                <div className="border-t border-border pt-3 space-y-3">
                  <Label className="text-sm">Sorular</Label>
                  {form.questions.map((q, i) => (
                    <div key={`q-${i}`} className="p-3 rounded border border-border space-y-2">
                      <div className="flex gap-2">
                        <Input placeholder="Soru metni" value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} />
                        <Select value={q.type} onValueChange={(v) => setQ(i, { type: v })}>
                          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(QTYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" onClick={() => setForm((f) => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }))}><Trash size={16} /></Button>
                      </div>
                      {q.type === "choice" && (
                        <Input placeholder="Seçenekler (virgül ile ayır)" value={(q.options || []).join(", ")} onChange={(e) => setQ(i, { options: e.target.value.split(",").map((s) => s.trim()) })} />
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, questions: [...f.questions, { text: "", type: "text", options: [] }] }))}><Plus size={14} className="mr-1" />Soru ekle</Button>
                </div>
                <div className="flex justify-end pt-2"><Button onClick={create} data-testid="s-submit">Yayınla</Button></div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((s) => (
          <Card key={s.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-display font-bold text-lg">{s.title}</div>
                  <div className="text-sm text-muted-foreground mt-1">{s.description}</div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {s.anonymous && <Badge variant="secondary">Anonim</Badge>}
                    {s.target_department && <Badge>{s.target_department}</Badge>}
                    {s.responded && <Badge variant="outline">Yanıtladınız</Badge>}
                    {typeof s.response_count === "number" && <Badge variant="secondary"><ChartLine size={12} className="mr-1" />{s.response_count} yanıt</Badge>}
                  </div>
                </div>
                {isAdmin && <Button size="icon" variant="ghost" onClick={() => del(s.id)} data-testid={`delete-survey-${s.id}`}><Trash size={16} className="text-destructive" /></Button>}
              </div>
              <div className="flex gap-2 justify-end mt-3">
                {!s.responded && !isAdmin && <Button size="sm" onClick={() => { setResponseFor(s); setAnswers({}); }} data-testid={`respond-${s.id}`}>Yanıtla</Button>}
                {isAdmin && <Button size="sm" variant="outline" onClick={() => openResponses(s)} data-testid={`view-${s.id}`}>Yanıtları Gör</Button>}
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-muted-foreground">Anket yok</div>}
      </div>

      <Dialog open={!!responseFor} onOpenChange={(o) => !o && setResponseFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{responseFor?.title || "Anket"}</DialogTitle></DialogHeader>
          {responseFor?.description && <div className="text-sm text-muted-foreground pb-2">{responseFor.description}</div>}
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(!responseFor?.questions || responseFor.questions.length === 0) && (
              <div className="text-sm text-muted-foreground p-4 text-center bg-muted/40 rounded">
                Bu ankete henüz soru eklenmemiş.
              </div>
            )}
            {responseFor?.questions?.map((q, i) => (
              <div key={`resp-q-${i}`}>
                <Label>{q.text}</Label>
                {q.type === "text" && <Textarea value={answers[i] || ""} onChange={(e) => setAnswers({...answers, [i]: e.target.value})} />}
                {q.type === "rating" && (
                  <div className="flex gap-1 mt-1">
                    {[1,2,3,4,5].map((n) => <Button key={n} size="sm" variant={answers[i] === n ? "default" : "outline"} onClick={() => setAnswers({...answers, [i]: n})}>{n}</Button>)}
                  </div>
                )}
                {q.type === "yesno" && (
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" variant={answers[i] === "Evet" ? "default" : "outline"} onClick={() => setAnswers({...answers, [i]: "Evet"})}>Evet</Button>
                    <Button size="sm" variant={answers[i] === "Hayır" ? "default" : "outline"} onClick={() => setAnswers({...answers, [i]: "Hayır"})}>Hayır</Button>
                  </div>
                )}
                {q.type === "choice" && (
                  <Select value={answers[i] || ""} onValueChange={(v) => setAnswers({...answers, [i]: v})}>
                    <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            ))}
            {responseFor?.questions?.length > 0 && (
              <Button onClick={respond} className="w-full" data-testid="respond-submit">Gönder</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewResponses} onOpenChange={(o) => !o && setViewResponses(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{viewResponses?.survey.title} — Yanıtlar ({viewResponses?.responses.length})</DialogTitle></DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto space-y-3">
            {viewResponses?.responses.map((r) => (
              <Card key={r.id || r.at}>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground mb-2">{r.user_name || "Anonim"} · {new Date(r.at).toLocaleString("tr-TR")}</div>
                  {viewResponses.survey.questions.map((q, qi) => (
                    <div key={`${r.id || r.at}-q-${qi}`} className="mb-2">
                      <div className="text-sm font-medium">{q.text}</div>
                      <div className="text-sm text-muted-foreground">{String(r.answers[qi] ?? "-")}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
